import { inject, Injectable } from '@angular/core';
import { EntityCollection } from '@ngrx/data';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { produce } from 'immer';
import {
  combineLatest,
  concatMap,
  defer,
  forkJoin,
  map,
  Observable,
  of,
  take,
  withLatestFrom,
} from 'rxjs';
import { SetRequired } from 'type-fest';

import {
  applyPropsPatch,
  CanvasElement,
  CanvasElementId,
  CanvasElementType,
  ColumnId,
  findByRefIds,
  getNamespaceId,
  PartialPropsOf,
  WrapperFilter,
  WrapperSearch,
} from '@blaze-runtime/engine-domain';
import { ColumnLinkInfo, ComparisonOperator } from '@blaze-shared/api-contracts-domain';
import { TablesCollectionService } from '@blaze-shared/datasets-data-access';
import {
  CanvasElementDataProperties,
  DataBindingSourceInfo,
  DataBindingSourceInfoColumn,
  DataBindingSourceType,
  findDisplayColumnKey,
  findIdColumnKey,
  findWorkflowGetSourceNode,
  getActiveBinding,
  getNewGetSourceNode,
  getOptionsListBindingForColumnFilter,
  getOptionsListBindingForDatasetColumn,
  getOptionsListBindingForRangeFilter,
  getOptionsListBindingForTagColumn,
  isBindingActive,
  ISearchAndFiltersWrapperService,
  PropertiesConfigInputListFiltersModel,
  SearchAndFiltersMode,
} from '@blaze-shared/elements-domain';
import { syncSelect } from '@blaze-shared/platform-data-access';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  BlazeTableSchema,
  ColumnLike,
  ColumnQuery,
  ColumnQueryLike,
  DatasetColumn,
  DatasetSchema,
  findColumn,
  findColumnKey,
  getColumnKey,
  getColumnQuery,
  hasBlazeTableFlag,
  matchColumn,
} from '@blaze-shared/schema-domain';
import { isNullish, isTruthy, SelectOption } from '@blaze-shared/shared-util';
import { StyleSheet } from '@blaze-shared/theming-domain';
import { findFirstNodeInWorkflowOfType, isGetNode, Workflow } from '@blaze-shared/workflow-domain';

import { swapElementType } from '../+state/elements/elements.actions';
import { ElementsFacade } from '../+state/elements/elements.facade';
import { getElementById } from '../+state/elements/elements.selectors';
import { RuntimeFacade } from '../+state/runtime/runtime.facade';
import { CanvasElementDefinitionInternal } from '../canvas-elements/element-definition';
import {
  arePropsWithData,
  hasDataProps,
  hasProps,
  hasSearchAndFilters,
  hasWrapperRefs,
} from '../canvas-elements/element-model/element-props-guards';
import { isDataFilter, isElementType } from '../canvas-elements/element-model/element-type-guards';
import { getWrapperRefs } from '../canvas-elements/element-model/wrapper-refs';
import { FieldInfo } from '../canvas-elements/field-info';
import { getUiRuntimeElement } from '../utils/runtime';

import { ElementDefinitionsService } from './element-definitions.service';
import { ElementTransformationService } from './element-transformation.service';
import { ElementStylesService, makeStyleConfigPropsForDefaultStyleSheet } from './styles';
import { WorkflowCrudService } from './workflow-crud.service';

export interface DatasetSchemaEntry {
  datasetId: string;
  schema?: DatasetSchema;
}

interface WrapperUpdateData {
  elementId: CanvasElementId;
  fields: FieldInfo[];
  inputWorkflow: Workflow;
  wrapperDefinition: CanvasElementDefinitionInternal;
}

@Injectable({ providedIn: 'root' })
export class SearchAndFiltersWrapperService extends ISearchAndFiltersWrapperService {
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly elementTransformationService = inject(ElementTransformationService);
  private readonly elementsFacade = inject(ElementsFacade);
  private readonly elementStylesService = inject(ElementStylesService);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);
  private readonly tablesCollectionService = inject(TablesCollectionService);
  private readonly workflowCrudService = inject(WorkflowCrudService);

  prepareUpdate(
    projectId: string,
    elementId: CanvasElementId,
    fallbackWorkflow: Workflow | undefined,
  ): Observable<WrapperUpdateData> {
    const inputWorkflow$ = defer(() => {
      const inputWorkflow = this.runtimeFacade.getWorkflowsByTarget(
        getUiRuntimeElement(elementId),
      )?.[0];
      if (!inputWorkflow && fallbackWorkflow) {
        return of(fallbackWorkflow);
      }
      if (inputWorkflow) {
        return of(inputWorkflow);
      }
      return this.workflowCrudService.createWorkflow({
        inputs: {},
        inputSchemas: [],
        isLongRunning: false,
        isServer: false,
        nodes: [],
        pageId: getNamespaceId(elementId),
        projectId,
        startNodeId: null,
      });
    });

    // Obtain schemas first, and then the workflow (so it's the most up to date one)
    return this.obtainNeededSchemas(elementId)
      .pipe(
        concatMap((usedSchemas) =>
          inputWorkflow$.pipe(map((inputWorkflow) => ({ usedSchemas, inputWorkflow }))),
        ),
      )
      .pipe(
        withLatestFrom(
          this.elementsFacade.entities$,
          this.elementStylesService.sheets$,
          this.tablesCollectionService.collection$,
        ),
        concatMap(([{ usedSchemas, inputWorkflow }, elements, styleSheets, tableDatasets]) => {
          const element = elements[elementId];
          if (!hasSearchAndFilters(element) || !hasDataProps(element)) {
            throw new Error('Element expected to have search and filter props');
          }

          // Mark workflow as a draft
          inputWorkflow = produce(inputWorkflow, (draft) => {
            draft.isDraft = true;
          });

          const firstGetNode = findFirstNodeInWorkflowOfType(inputWorkflow, isGetNode);
          const primaryDatasetId = firstGetNode?.datasetId;
          const { availableFields, availableFieldsForSearch } = this.getAvailableFields(
            primaryDatasetId,
            tableDatasets,
            usedSchemas,
          );

          const wrapperDefinition = this.elementDefinitionsService.getDefinitionSafe('wrapper');
          const searchAndFilters = element.props.content.searchAndFilters;
          const fields: FieldInfo[] = [];

          const hasSearch = searchAndFilters?.searchEnabled || false;
          const useUserSelectedSearchColumns =
            searchAndFilters?.useUserSelectedSearchColumns || false;
          const customSearchColumns = searchAndFilters?.searchColumns;
          const hasFilters =
            (searchAndFilters?.filters || []).length > 0 &&
            searchAndFilters?.mode !== SearchAndFiltersMode.FILTER_BUILDER;

          const valueDataSource = getActiveBinding(element.props.data, 'value')?.dataSource;
          if (!valueDataSource) {
            throw new Error('Missing value binding');
          }

          const columns = valueDataSource.columns;
          if (hasSearch || hasFilters) {
            // Just for safety - create Get node if workflow is new & empty
            if (
              !inputWorkflow.startNodeId &&
              inputWorkflow.nodes.length === 0 &&
              valueDataSource.sourceType === 'dataset' &&
              valueDataSource.id
            ) {
              inputWorkflow = produce(inputWorkflow, (draft) => {
                const node = getNewGetSourceNode(valueDataSource.id);
                draft.nodes = [node];
                draft.startNodeId = node.id;
              });
            }
          }

          if (hasSearch) {
            fields.push(
              this.prepareSearchField(
                element,
                styleSheets,
                availableFieldsForSearch,
                useUserSelectedSearchColumns,
                customSearchColumns,
              ),
            );
          }

          if (hasFilters) {
            // The filters are applied on the primary dataset
            const availableColumns =
              (primaryDatasetId
                ? this.schemaService.getSchema(primaryDatasetId)?.columns
                : availableFields) ?? columns;
            const filterFields = (searchAndFilters?.filters || [])
              .filter(
                (
                  filter,
                ): filter is SetRequired<PropertiesConfigInputListFiltersModel, 'filterField'> => {
                  const columnQuery = this.schemaService.getColumnQuery(filter.filterField);

                  const matchingColumn = findColumn(availableFields, columnQuery);
                  // If field is not available -> omit it
                  if (
                    (!columnQuery.name && !columnQuery.id) ||
                    (availableFields && !matchingColumn)
                  ) {
                    return false;
                  }
                  // If filter field exists in column list -> prepare field for it
                  return !!matchingColumn;
                },
              )
              .map((filter) =>
                this.prepareFilterField(
                  filter,
                  elements,
                  element,
                  usedSchemas,
                  availableColumns,
                  styleSheets,
                  availableFieldsForSearch,
                  primaryDatasetId,
                  inputWorkflow?.id,
                ),
              )
              .filter(isTruthy);

            fields.push(...filterFields);
          }

          return of({ elementId, wrapperDefinition, inputWorkflow, fields });
        }),
      );
  }

  getAvailableSearchFields(
    elementId: CanvasElementId,
    workflowId: string,
  ): Observable<{
    availableFields: DatasetColumn[] | undefined;
    availableFieldsForSearch: DatasetColumn[] | undefined;
  }> {
    const workflow = this.runtimeFacade.getWorkflowById(workflowId) ?? undefined;
    const firstGetNode = findFirstNodeInWorkflowOfType(workflow, isGetNode);
    const primaryDatasetId = firstGetNode?.datasetId;

    return this.obtainNeededSchemas(elementId).pipe(
      withLatestFrom(this.tablesCollectionService.collection$),
      map(([usedSchemas, tables]) => {
        return this.getAvailableFields(primaryDatasetId, tables, usedSchemas);
      }),
    );
  }

  getAvailableFilterFields(
    elementId: CanvasElementId,
    workflowId: string,
  ): Observable<{
    availableFields: DatasetColumn[] | undefined;
  }> {
    return this.getAvailableSearchFields(elementId, workflowId).pipe(
      map(({ availableFields }) => ({ availableFields })),
    );
  }

  getControlTypesForField(
    filterColumn: DatasetColumn | undefined,
    currentElementType: CanvasElementType,
  ): Observable<SelectOption<string>[]> {
    if (!filterColumn) {
      return of([]);
    }

    return of(
      this.elementTransformationService.getTransformationListOptions(currentElementType, {
        filterColumnDataType: filterColumn.dataType,
        isSearchAndFilterField: true,
      }),
    );
  }

  getFilterControlElement(
    containerElementId: CanvasElementId | undefined,
    fieldId: number | undefined,
  ): CanvasElement | undefined {
    if (!containerElementId || fieldId === undefined) {
      return undefined;
    }

    const element = syncSelect(this.store, getElementById(containerElementId));
    if (!hasWrapperRefs(element)) {
      return undefined;
    }

    const filterElementRef = findByRefIds(
      element.props.wrapper.refs,
      WrapperFilter.getRefIds(fieldId?.toString()),
    );

    return filterElementRef ? syncSelect(this.store, getElementById(filterElementRef)) : undefined;
  }

  getFilterControlType(
    containerElementId: CanvasElementId | undefined,
    fieldId: number | undefined,
  ): { id?: CanvasElementId; type?: CanvasElementType } | undefined {
    const filterElement = this.getFilterControlElement(containerElementId, fieldId);
    if (!filterElement) {
      return undefined;
    }
    const { id, type } = filterElement;
    return { id, type };
  }

  updateFilterElementType(
    containerElementId: CanvasElementId | undefined,
    fieldId: number | undefined,
    targetType: CanvasElementType,
  ): void {
    if (fieldId === undefined) {
      return;
    }
    const { id, type } = this.getFilterControlType(containerElementId, fieldId) ?? {};
    if (!id || !type || type === targetType) {
      return;
    }
    this.store.dispatch(swapElementType({ elementId: id, sourceType: type, targetType }));
  }

  private getAvailableFields(
    primaryDatasetId: string | undefined,
    tableDatasets: EntityCollection<BlazeTableSchema>,
    usedSchemas: DatasetSchemaEntry[],
  ): {
    availableFields: DatasetColumn[] | undefined;
    availableFieldsForSearch: DatasetColumn[] | undefined;
  } {
    let availableFields: DatasetColumn[] | undefined;
    let availableFieldsForSearch: DatasetColumn[] | undefined;

    if (primaryDatasetId) {
      const firstGetNodeSchema = this.schemaService.getSchema(primaryDatasetId);
      if (firstGetNodeSchema) {
        const isBlazeTable = (tableDatasets.ids as string[]).includes(primaryDatasetId);

        // Limit columns to the ones present in the dataset provided in the GET node (due to the filtering being made on that node)
        availableFields = firstGetNodeSchema.columns.filter((column) => !column.isInternal);

        // Text searching in fields works for string fields and linked string fields (for Blaze Table all linked fields work)
        availableFieldsForSearch = firstGetNodeSchema.columns.filter((column) => {
          if (column.isInternal) {
            return false;
          } else if (column.dataType === 'String') {
            return true;
          } else if (column.dataType === 'Link') {
            const linkedTableSchema = usedSchemas.find(
              (schema) => schema.datasetId === column.link?.tableId,
            )?.schema;
            const linkedField =
              linkedTableSchema &&
              findColumn(linkedTableSchema.columns, findDisplayColumnKey(linkedTableSchema));
            const isLinkedFieldString = linkedField?.dataType === 'String';
            return isBlazeTable || isLinkedFieldString;
          } else if (column.dataType === 'Tag') {
            return true;
          } else {
            return false;
          }
        });
      } else {
        console.warn(`Schema missing for first GET node`);
      }
    }

    return { availableFields, availableFieldsForSearch };
  }

  /**
   * Checks whether given filter is specified to contain predefined values.
   */
  private isPredefinedOperator(
    filter: SetRequired<PropertiesConfigInputListFiltersModel, 'filterField'>,
  ): boolean {
    return filter?.filterOperatorType === 'Predefined';
  }

  private obtainNeededSchemas(elementId: CanvasElementId): Observable<DatasetSchemaEntry[]> {
    return this.elementsFacade.getElement(elementId).pipe(
      take(1),
      concatMap((element) => {
        if (!element) {
          throw new Error('Could not find element');
        }
        if (!hasSearchAndFilters(element) || !hasProps(element) || !hasDataProps(element)) {
          throw new Error('Element expected to have searchAndFilters settings');
        }

        const searchAndFilters = element.props.content.searchAndFilters;

        const valueDataSource = element.props.data.find(({ key }) => key === 'value')?.dataSource;
        if (!valueDataSource) {
          console.warn('Value binding missing');
          return of({ columns: [], searchAndFilters });
        }

        let columns$: Observable<DataBindingSourceInfoColumn[]> = of(valueDataSource.columns); // Fallback to stored columns if source not supported
        // Try to use runtime schema
        const useLiveSchemaFor: DataBindingSourceType[] = ['context', 'dataset', 'data-filter'];
        if (useLiveSchemaFor.includes(valueDataSource.sourceType)) {
          columns$ = this.schemaService
            .obtainSchema(
              valueDataSource.id,
              valueDataSource.sourceType === 'context' ||
                valueDataSource.sourceType === 'data-filter',
            )
            .pipe(
              map((schema) => {
                if (schema?.columns && schema.columns.length > 0) {
                  return schema.columns;
                }

                return valueDataSource.columns; // Falback to stored columns if schema missing
              }),
            );
        }

        return combineLatest({
          columns: columns$,
          searchAndFilters: of(searchAndFilters),
        });
      }),
      concatMap(({ columns, searchAndFilters }) => {
        const tableIdsToFetch = new Set<string>();

        if (searchAndFilters.searchEnabled) {
          // If search enabled - fetch all link schemas
          columns
            .filter((column) => column.dataType === 'Link')
            .map((column) => column.link?.tableId)
            .filter((tableId): tableId is string => !!tableId)
            .forEach((tableId) => tableIdsToFetch.add(tableId));
        }

        // Fetch schemas for all columns by which filtering is done
        (searchAndFilters?.filters || [])
          .map((filter) => filter.filterField)
          .filter((fieldName): fieldName is string => !!fieldName)
          .map(
            (fieldName) =>
              findColumn(columns, this.schemaService.getColumnQuery(fieldName))?.link?.tableId,
          )
          .filter((tableId): tableId is string => !!tableId)
          .forEach((tableId) => tableIdsToFetch.add(tableId));

        const schemas: Observable<DatasetSchemaEntry>[] = [...tableIdsToFetch].map((tableId) =>
          this.schemaService
            .obtainSchema<DatasetSchema>(tableId)
            .pipe(map((schema) => ({ datasetId: tableId, schema }))),
        );

        return schemas.length > 0 ? forkJoin(schemas) : of([]);
      }),
    );
  }

  private prepareSearchField(
    element: CanvasElement,
    styleSheets: StyleSheet[],
    availableFieldsForSearch: ColumnLike[] | undefined,
    useUserSelectedSearchColumns: boolean | undefined,
    searchColumns: (ColumnId | string)[] | undefined,
  ): FieldInfo {
    const elementDefinition = this.elementDefinitionsService.getDefinitionSafe('textInput');
    const initProps = this.elementDefinitionsService
      .getPropsBuilder('textInput', {
        content: {
          input: {
            format: 'text',
            placeholder: 'Search',
          },
        },
        ...makeStyleConfigPropsForDefaultStyleSheet(styleSheets, elementDefinition.type),
      })
      ?.setMargin(!isDataFilter(element) ? '$t.spacingSearchAndFilters' : '$t.spacingInput')
      .build();

    const fieldQueries: (ColumnQueryLike | undefined)[] = [];

    let searchInFields: string[] = [];

    if (!useUserSelectedSearchColumns) {
      if (isElementType(element, 'list')) {
        fieldQueries.push(
          element.props.content.fields?.heading,
          element.props.content.fields?.description,
          ...(element.props.content.customFields || []).map((field) => field.column),
        );
      } else if (isElementType(element, 'table')) {
        fieldQueries.push(
          ...(element.props.content.columns || []).filter((column) => column.visible),
          ...(element.props.content.table?.groupBy || []).map((col) => col.field).filter(Boolean),
        );
      } else if (isElementType(element, 'kanbanBoard')) {
        fieldQueries.push(
          element.props.content.fields?.columnTitle,
          element.props.content.fields?.cardTitle,
          element.props.content.fields?.cardDescription,
          ...(element.props.content.customFields || []).map((field) => field.column),
        );
      } else if (isElementType(element, 'ganttChart')) {
        fieldQueries.push(
          element.props.content.fields?.task,
          element.props.content.fields?.category,
          ...(element.props.content.customFields || []).map((field) => field.column),
        );
      } else if (isElementType(element, 'map')) {
        fieldQueries.push(
          element.props.content.markers?.title,
          element.props.content.markers?.field,
          ...(element.props.content.markers?.customFields || []).map((field) => field.column),
          element.props.content.card?.title,
          element.props.content.card?.description,
          ...(element.props.content.card?.customFields || []).map((field) => field.column),
        );
      } else if (isDataFilter(element) && hasDataProps(element)) {
        const workflowId = getActiveBinding(element.props.data, 'value')?.dataSource?.id;
        if (!workflowId) {
          throw new Error('Missing data-filter workflow');
        }
        const workflow = this.runtimeFacade.getWorkflowById(workflowId);
        if (!workflow) {
          throw new Error('Missing data-filter workflow');
        }

        const datasetId = findWorkflowGetSourceNode(workflow)?.datasetId;
        if (datasetId) {
          const columns = this.schemaService.getSchema(datasetId)?.columns.map((c) => c.id) ?? [];
          fieldQueries.push(...columns);
        }
      }

      const filteredFieldQueries = fieldQueries.filter(
        (field): field is ColumnQueryLike => !!field,
      );

      const fieldsToSearch: ColumnQueryLike[] = availableFieldsForSearch
        ? filteredFieldQueries.filter((field) => findColumn(availableFieldsForSearch, field))
        : filteredFieldQueries;

      searchInFields = fieldsToSearch.map((field) => getColumnKey(field));
    } else {
      const fieldsToSearch = availableFieldsForSearch?.map((field) => getColumnKey(field)) ?? [];

      if (!searchColumns) {
        searchInFields = fieldsToSearch;
      } else if (searchColumns.length) {
        searchInFields = fieldsToSearch.filter((field) => searchColumns.includes(field));
      }
    }

    return {
      refIds: WrapperSearch.getRefIds(),
      type: 'search',
      searchInFields,
      elementDefinition,
      initProps,
    };
  }

  private prepareFilterField(
    filter: SetRequired<PropertiesConfigInputListFiltersModel, 'filterField'>,
    elements: Dictionary<CanvasElement>,
    element: CanvasElement,
    usedSchemas: DatasetSchemaEntry[],
    columns: DatasetColumn[],
    styleSheets: StyleSheet[],
    availableFieldsForSearch: DatasetColumn[] | undefined,
    primaryDatasetId: string | undefined,
    workflowId: string | undefined,
  ): FieldInfo | undefined {
    if (isNullish(filter.id)) {
      return undefined;
    }
    const fieldRefIds = WrapperFilter.getRefIds(String(filter.id));
    const columnQuery = this.schemaService.getColumnQuery(filter.filterField);
    const isPredefinedOperator = this.isPredefinedOperator(filter);

    // Restore field's element id from refs
    const column = findColumn(columns, filter.filterField);
    const fieldElementId = findByRefIds(getWrapperRefs(element), fieldRefIds);
    const currentElement = fieldElementId ? elements[fieldElementId] : undefined;
    const { defaultElementType, initProps, patchProps } = getDefaultElementData(
      column,
      styleSheets,
      isPredefinedOperator,
      currentElement?.type,
    );
    const currentElementType: CanvasElementType =
      filter.__fieldTypeChanged || isPredefinedOperator
        ? defaultElementType
        : (currentElement?.type ?? defaultElementType);

    const elementDefinition = this.elementDefinitionsService.getDefinitionSafe(currentElementType);

    // Get current state of options list
    const currentData = hasDataProps(currentElement) ? currentElement.props.data : undefined;
    const optionsList = currentData?.find(({ key }) => key === 'optionsList');

    const hasOptionsListInput =
      elementDefinition.ioConfig?.inputs?.some((input) => input.name === 'optionsList') || false;
    const newData = hasOptionsListInput
      ? this.prepareOptionsListUpdate(
          filter,
          columnQuery,
          optionsList,
          primaryDatasetId,
          usedSchemas,
          columns,
          workflowId,
        )
      : undefined;

    // Include init props if new element is created
    const baseUpdateProps = !currentElement ? applyPropsPatch(initProps, patchProps) : patchProps;

    const updateProps = this.elementDefinitionsService
      .getPropsBuilder(currentElementType, baseUpdateProps)
      ?.setPlaceholder(filter.placeholder)
      .setDefaultValue('placeholder')
      .setInitialToggleState(filter.toggleDefault)
      .setBinding(newData, true)
      .setMargin(!isDataFilter(element) ? '$t.spacingSearchAndFilters' : '$t.spacingInput')
      .build() as PartialPropsOf<
      | 'dropdown'
      | 'tags'
      | 'radio'
      | 'checkboxInput'
      | 'checkbox'
      | 'textInput'
      | 'datetimeInput'
      | 'toggleButton'
    >; // TODO: narrow down these

    const optionsListBinding = arePropsWithData(updateProps)
      ? updateProps.data.find((binding) => binding?.key === 'optionsList')
      : undefined;
    // If binding enabled -> use binding; otherwise use static value
    const bindingField = 'value';
    // If no options bindings - don't use data value field, otherwise fallback to 'value' field (used for static values)
    const defaultField = !optionsListBinding ? undefined : 'value';
    const filterDataValueField = isBindingActive(optionsListBinding)
      ? optionsListBinding.bindings?.[bindingField]
      : defaultField;

    // Get the comparison operator
    let filterOperator: ComparisonOperator = filter.operator ?? ComparisonOperator.Eq;
    if (!filter.operator) {
      if (currentElementType === 'textInput') {
        // Use ContainsText operator if the left side column supports it
        if (availableFieldsForSearch?.find((field) => matchColumn(field, columnQuery))) {
          filterOperator = ComparisonOperator.ContainsText;
        } else {
          filterOperator = ComparisonOperator.Eq;
        }
      }
    }
    if (currentElementType === 'tags' || currentElementType === 'checkbox') {
      filterOperator = ComparisonOperator.In;
    }

    // Date range dropdown
    return {
      elementDefinition,
      elementTypeChangedFieldId: filter.__fieldTypeChanged ? filter.id : undefined,
      fieldColumnDataType: column?.dataType,
      fieldId: columnQuery.id,
      fieldName: columnQuery.name,
      filterDataValueField,
      filterOperator,
      filterOperatorType: filter.filterOperatorType,
      predefinedFilterOperators: filter.predefinedOperators,
      refIds: fieldRefIds,
      type: 'filter',
      updateProps,
    };
  }

  private prepareOptionsListUpdate(
    filter: SetRequired<PropertiesConfigInputListFiltersModel, 'filterField'>,
    columnQuery: ColumnQuery,
    optionsList: CanvasElementDataProperties | undefined,
    primaryDatasetId: string | undefined,
    usedSchemas: DatasetSchemaEntry[],
    columns: DatasetColumn[],
    workflowId: string | undefined,
  ): CanvasElementDataProperties | undefined {
    const column = findColumn(columns, columnQuery);
    if (!column) {
      throw new Error(`Column ${JSON.stringify(columnQuery)} could not be found`);
    }

    // Was the options list modified by the user in a way which should be preserved
    const isOptionsListDirty =
      optionsList?.enabled === false || // Binding explicitly disabled by user
      (column.link && this.shouldKeepOptionsListBinding(column.link.tableId, optionsList)) ||
      (column.dataType !== 'Tag' && // Tag column can always be updated
        primaryDatasetId &&
        this.shouldKeepOptionsListBinding(primaryDatasetId, optionsList) &&
        matchColumn(column, getColumnQuery(optionsList?.bindings?.value || '')));

    const isPredefinedOperator = this.isPredefinedOperator(filter);
    if (isOptionsListDirty) {
      return optionsList;
    } else if (isPredefinedOperator && primaryDatasetId && workflowId) {
      return getOptionsListBindingForRangeFilter(filter, workflowId, getColumnKey(column));
    } else if (!isPredefinedOperator && primaryDatasetId && workflowId) {
      return getOptionsListBindingForColumnFilter(workflowId, getColumnKey(column));
    } else if (column.link) {
      // Link column
      const schema = usedSchemas.find(
        (schema) => schema.datasetId === column.link?.tableId,
      )?.schema;
      return this.prepareOptionsListUpdateForLink(optionsList, column.link, schema);
    } else if (column.dataType === 'Tag') {
      // Tags column
      return this.prepareOptionsListUpdateForTags(primaryDatasetId, getColumnKey(column));
    } else if (primaryDatasetId) {
      // Other column types
      return this.prepareOptionsListUpdateForOthers(
        optionsList,
        primaryDatasetId,
        columns,
        getColumnKey(column),
      );
    }
    return undefined;
  }

  private prepareOptionsListUpdateForLink(
    optionsList: CanvasElementDataProperties | undefined,
    link: ColumnLinkInfo,
    schema: DatasetSchema | undefined,
  ): CanvasElementDataProperties | undefined {
    if (this.shouldKeepOptionsListBinding(link.tableId, optionsList)) {
      return optionsList;
    }

    if (!schema) {
      console.warn(`Schema for tableId ${link.tableId} could not be find`);
      return;
    }

    const schemaLabelBinding = findDisplayColumnKey(schema);
    const currentLabelBinding = optionsList?.bindings?.label;

    // Resolve value binding key (use id if available)
    const linkSchema = this.schemaService.getSchema(link.tableId);
    const linkColumnName = hasBlazeTableFlag(linkSchema)
      ? this.schemaService.getIdColumnName(link.tableId)
      : link.columnName;
    // Store column ID if available
    const linkColumnKey = findColumnKey(linkSchema?.columns, linkColumnName);
    const valueBinding = linkColumnKey || findIdColumnKey(schema);

    const hasValueBindingChanged = optionsList?.bindings?.value !== valueBinding;
    const hasLabelBindingChanged = currentLabelBinding !== schemaLabelBinding;
    const shouldResetLabelBinding =
      hasValueBindingChanged || hasLabelBindingChanged || !currentLabelBinding;

    // If value or label binding changed or label binding missing -> set it to the default based on schema
    const labelBinding = shouldResetLabelBinding ? schemaLabelBinding : currentLabelBinding;

    const bindings: Record<string, string> = { value: valueBinding, label: labelBinding };
    const dataSource: DataBindingSourceInfo = {
      ...schema,
      sourceType: 'dataset',
      projectColumns: true,
    };

    return { key: 'optionsList', bindings, dataSource, enabled: true };
  }

  private prepareOptionsListUpdateForTags(
    primaryDatasetId: string | undefined,
    columnIdOrName: string,
  ): CanvasElementDataProperties | undefined {
    if (!primaryDatasetId) {
      console.warn('Could not identify primary dataset');
      return;
    }

    return getOptionsListBindingForTagColumn(primaryDatasetId, columnIdOrName);
  }

  private prepareOptionsListUpdateForOthers(
    optionsList: CanvasElementDataProperties | undefined,
    primaryDatasetId: string,
    columns: DatasetColumn[],
    columnIdOrName: string,
  ): CanvasElementDataProperties | undefined {
    // Preserve options list if binding similar
    if (
      this.shouldKeepOptionsListBinding(primaryDatasetId, optionsList) &&
      optionsList?.bindings?.value === columnIdOrName &&
      optionsList.enabled === false // Binding explicitly disabled by user
    ) {
      return optionsList;
    } else {
      const bindings: Record<string, string> = { value: columnIdOrName, label: columnIdOrName };
      return getOptionsListBindingForDatasetColumn(
        primaryDatasetId,
        columnIdOrName,
        bindings,
        columns,
      );
    }
  }

  /**
   * If the binding is made to the workflow, the underlying dataset is compared and kept if it matches the updated table id
   * This is made so the customizations made to the filtering settings are kept.
   * @param tableId current table id for the field
   * @param optionsList current optionsList binding
   * @returns whether the optionsList binding should be kept without overwriting it
   */
  private shouldKeepOptionsListBinding(
    tableId: string,
    optionsList: CanvasElementDataProperties | undefined,
  ): boolean {
    if (!optionsList) {
      return false;
    } else if (optionsList.isCustomSelection === true) {
      return true;
    }

    const dataSource = optionsList.dataSource;
    if (!dataSource || dataSource.sourceType !== 'context') {
      return false;
    }

    const workflow = this.runtimeFacade.getWorkflowById(dataSource.id);
    if (!workflow) {
      return false;
    }

    const getNode = findFirstNodeInWorkflowOfType(workflow, isGetNode);
    if (!getNode) {
      return false;
    }

    return getNode.datasetId === tableId;
  }
}

interface DefaultElementData<T extends CanvasElementType> {
  defaultElementType: T;
  initProps?: PartialPropsOf<T>;
  patchProps?: PartialPropsOf<T>;
}

function createDefaultElementData<T extends CanvasElementType>(
  defaultElementType: T,
  initProps?: PartialPropsOf<T>,
  patchProps?: PartialPropsOf<T>,
): DefaultElementData<T> {
  return { defaultElementType, initProps, patchProps };
}

function getDefaultElementData(
  column: DatasetColumn | undefined,
  styleSheets: StyleSheet[],
  isPredefinedOperator: boolean,
  currentElementType?: CanvasElementType,
): DefaultElementData<CanvasElementType> {
  if (isPredefinedOperator) {
    return createDefaultElementData(
      'dropdown',
      makeStyleConfigPropsForDefaultStyleSheet(styleSheets, 'dropdown'),
    );
  }

  switch (column?.dataType) {
    case 'Date':
      return createDefaultElementData(
        'datetimeInput',
        makeStyleConfigPropsForDefaultStyleSheet(styleSheets, 'datetimeInput'),
        {
          content: {
            date: { enabled: true },
            time: { enabled: false },
          },
        },
      );
    case 'DateTime':
      return createDefaultElementData(
        'datetimeInput',
        makeStyleConfigPropsForDefaultStyleSheet(styleSheets, 'datetimeInput'),
        {
          content: {
            date: { enabled: true },
            time: { enabled: true },
          },
        },
      );
    case 'Number':
      return createDefaultElementData(
        'textInput',
        makeStyleConfigPropsForDefaultStyleSheet(styleSheets, 'textInput'),
        {
          content: {
            input: {
              format: 'number',
              precision: null,
            },
          },
        },
      );
    case 'String':
      if (currentElementType === 'textInput') {
        return createDefaultElementData(
          'textInput',
          makeStyleConfigPropsForDefaultStyleSheet(styleSheets, 'textInput'),
          {
            content: {
              input: {
                format: 'text',
                precision: null,
              },
            },
          },
        );
      }
  }

  return createDefaultElementData(
    'dropdown',
    makeStyleConfigPropsForDefaultStyleSheet(styleSheets, 'dropdown'),
  );
}
